// ---- Browser Constants ----
export enum BROWSERS {
	OperaMini = "Opera Mini",
	Opera = "Opera",
	BlackBerry = "BlackBerry",
	IEMobile = "IEMobile",
	Edge = "Edge",
	FB = "Facebook App",
	Chrome = "Chrome",
	ChromeIOS = "Chrome (iOS)",
	UC = "UC Browser",
	FirefoxIOS = "Firefox (iOS)",
	SafariMobile = "Safari Mobile",
	Safari = "Safari",
	AndroidMobile = "Android Browser",
	Konqueror = "Konqueror",
	Firefox = "Firefox",
	IE = "IE",
	Mozilla = "Mozilla",
	Brave = "Brave",
}

// ---- Returned structure ----
export interface DeviceInfo {
	dimensions: { width: number; height: number }
	dimensions_in_pixels: { width: number; height: number }
	dpi: string
	device: string
	browser: string
	browser_version: number | null
	os: string
}

/**
 * Main: Device Info
 */
export const getDeviceInfo = (win: Partial<Window> = {}): DeviceInfo => {
	const userAgent = win.navigator?.userAgent ?? ""
	const vendor = win.navigator?.vendor ?? ""

	/**
	 * Browser Name
	 */
	const getBrowser = (): BROWSERS | "" => {
		// Brave doesn't expose itself via UA
		if ((win.navigator as any)?.brave) return BROWSERS.Brave

		switch (true) {
			case userAgent.includes(" OPR/"):
				return userAgent.includes("Mini")
					? BROWSERS.OperaMini
					: BROWSERS.Opera
			case /(BlackBerry|PlayBook|BB10)/i.test(userAgent):
				return BROWSERS.BlackBerry
			case userAgent.includes("IEMobile") ||
				userAgent.includes("WPDesktop"):
				return BROWSERS.IEMobile
			case userAgent.includes("FBIOS"):
				return BROWSERS.FB
			case userAgent.includes("Chrome"):
				return BROWSERS.Chrome
			case userAgent.includes("CriOS"):
				return BROWSERS.ChromeIOS
			case userAgent.includes("Edge"):
				return BROWSERS.Edge
			case userAgent.includes("UCWEB") || userAgent.includes("UCBrowser"):
				return BROWSERS.UC
			case userAgent.includes("FxiOS"):
				return BROWSERS.FirefoxIOS
			case vendor.includes("Apple"):
				return userAgent.includes("Mobile")
					? BROWSERS.SafariMobile
					: BROWSERS.Safari
			case userAgent.includes("Android"):
				return BROWSERS.AndroidMobile
			case userAgent.includes("Konqueror"):
				return BROWSERS.Konqueror
			case userAgent.includes("Firefox"):
				return BROWSERS.Firefox
			case userAgent.includes("MSIE") || userAgent.includes("Trident/"):
				return BROWSERS.IE
			case userAgent.includes("Gecko"):
				return BROWSERS.Mozilla
			default:
				return ""
		}
	}

	/**
	 * Browser Version
	 */
	const getBrowserVersion = (browser: BROWSERS | ""): number | null => {
		const versionRegEx: Partial<Record<BROWSERS, RegExp>> = {
			[BROWSERS.IEMobile]: /rv:(\d+(\.\d+)?)/,
			[BROWSERS.Edge]: /Edge\/(\d+(\.\d+)?)/,
			[BROWSERS.Chrome]: /Chrome\/(\d+(\.\d+)?)/,
			[BROWSERS.ChromeIOS]: /CriOS\/(\d+(\.\d+)?)/,
			[BROWSERS.UC]: /(UCBrowser|UCWEB)\/(\d+(\.\d+)?)/,
			[BROWSERS.Safari]: /Version\/(\d+(\.\d+)?)/,
			[BROWSERS.SafariMobile]: /Version\/(\d+(\.\d+)?)/,
			[BROWSERS.Opera]: /(Opera|OPR)\/(\d+(\.\d+)?)/,
			[BROWSERS.Firefox]: /Firefox\/(\d+(\.\d+)?)/,
			[BROWSERS.FirefoxIOS]: /FxiOS\/(\d+(\.\d+)?)/,
			[BROWSERS.Konqueror]: /Konqueror:(\d+(\.\d+)?)/,
			[BROWSERS.BlackBerry]: /BlackBerry (\d+(\.\d+)?)/,
			[BROWSERS.AndroidMobile]: /android\s(\d+(\.\d+)?)/,
			[BROWSERS.IE]: /(rv:|MSIE )(\d+(\.\d+)?)/,
			[BROWSERS.Mozilla]: /rv:(\d+(\.\d+)?)/,
			[BROWSERS.OperaMini]: /OPR\/(\d+(\.\d+)?)/,
			[BROWSERS.Brave]: /Chrome\/(\d+(\.\d+)?)/, // Brave mimics Chrome UA
			[BROWSERS.FB]: /FBAV\/(\d+(\.\d+)?)/,
		}

		if (!browser) return null

		const regex = versionRegEx[browser]
		if (!regex) return null

		const matches = userAgent.match(regex)
		return matches ? parseFloat(matches[matches.length - 2]) : null
	}

	/**
	 * OS Detection
	 */
	const getOS = (): string => {
		switch (true) {
			case /Windows/i.test(userAgent):
				return /Phone/.test(userAgent) || /WPDesktop/.test(userAgent)
					? "Windows Phone"
					: "Windows"
			case /(iPhone|iPad|iPod)/.test(userAgent):
				return "iOS"
			case /Android/.test(userAgent):
				return "Android"
			case /(BlackBerry|PlayBook|BB10)/i.test(userAgent):
				return "BlackBerry"
			case /Mac/i.test(userAgent):
				return "Mac OS X"
			case /Linux/.test(userAgent):
				return "Linux"
			case /CrOS/.test(userAgent):
				return "Chrome OS"
			default:
				return ""
		}
	}

	/**
	 * Screen Dimensions
	 */
	const getDimensions = () => {
		const width = win.screen?.width ?? 0
		const height = win.screen?.height ?? 0
		const dpr = win.devicePixelRatio ?? 1

		const roundedDimension = (dim: number) => {
			const mul = Math.round(dim) * dpr
			const mod = mul % 10
			return mul + (mod > 0 ? 10 : 0) - mod
		}

		return {
			dimensions: { width, height },
			dimensions_in_pixels: {
				width: roundedDimension(width),
				height: roundedDimension(height),
			},
			dpi: dpr.toString(),
		}
	}

	/**
	 * Get Device (Mobile only)
	 */
	const getDevice = (userAgent: string = ""): string => {
		switch (true) {
			case /Windows Phone/i.test(userAgent) ||
				/WPDesktop/.test(userAgent):
				return "Windows Phone"
			case /iPad/.test(userAgent):
				return "iPad"
			case /iPod/.test(userAgent):
				return "iPod Touch"
			case /iPhone/.test(userAgent):
				return "iPhone"
			case /(BlackBerry|PlayBook|BB10)/i.test(userAgent):
				return "BlackBerry"
			case /Android/.test(userAgent):
				return "Android"
			default:
				return "Desktop"
		}
	}

	const browser = getBrowser()

	return {
		...getDimensions(),
		device: getDevice(userAgent),
		browser,
		browser_version: getBrowserVersion(browser),
		os: getOS(),
	}
}
